import { useState, useEffect } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import CompanySelector from "../components/ui/CompanySelector.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import {
  getContractsForCompany,
  getContractEntriesAdmin,
  createContract,
  updateContract,
  deleteContract,
  addManualEntry,
  deleteManualEntry,
} from "../services/billingService.js";

const TYPE_META = {
  HOUR_BANK:        { label: "Banque d'heures",   icon: "bi-clock-history",     color: "#0EA5E9" },
  SERVICE_CONTRACT: { label: "Contrat de service", icon: "bi-file-earmark-text", color: "#7C3AED" },
  HOURLY:           { label: "Horaire",            icon: "bi-stopwatch",         color: "#F59E0B" },
};

const PERIOD_LABELS = {
  ON_DEMAND: "À la demande", WEEKLY: "Hebdomadaire", BIWEEKLY: "Bimensuel", MONTHLY: "Mensuel",
  QUARTERLY: "Trimestriel", SEMIANNUAL: "Semestriel", YEARLY: "Annuel",
};

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" });
}

function fmtHours(h) {
  const n = Number(h);
  if (Number.isNaN(n)) return "0h";
  return `${n >= 0 ? "" : "-"}${Math.abs(n)}h`.replace(".", ",");
}

// ─────────────────────────────────────────────
//  MODALE — Créer un contrat
// ─────────────────────────────────────────────
function CreateContractModal({ companyId, companyName, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    type: "HOUR_BANK",
    startDate: "",
    endDate: "",
    active: true,
    isDefault: false,
    taxable: true,
    notes: "",
    totalHoursAllocated: "",
    rolloverEnabled: false,
    billingPeriod: "YEARLY",
    hourlyRate: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setError("");
    if (!form.name.trim()) return setError("Le nom est obligatoire.");

    setLoading(true);
    try {
      const payload = {
        companyId,
        name: form.name.trim(),
        type: form.type,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        active: form.active,
        isDefault: form.isDefault,
        taxable: form.taxable,
        notes: form.notes || null,
        ...(form.type === "HOUR_BANK" ? {
          totalHoursAllocated: parseFloat(form.totalHoursAllocated) || 0,
          rolloverEnabled: form.rolloverEnabled,
        } : {}),
        ...(form.type === "SERVICE_CONTRACT" || form.type === "HOURLY" ? { billingPeriod: form.billingPeriod } : {}),
        ...(form.type === "HOURLY" ? { hourlyRate: parseFloat(form.hourlyRate) || 0 } : {}),
      };
      await createContract(payload);
      onSaved();
    } catch (err) {
      setError(err.message || "Erreur lors de la création.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>Nouveau contrat</h3>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>

        <div className="modal-body">
          {error && <div className="form-error"><i className="bi bi-exclamation-triangle me-2" />{error}</div>}

          <div className="modal-section-title"><i className="bi bi-file-earmark-text me-2" />Détails du contrat</div>

          <div className="form-group">
            <label className="itm-label">Client</label>
            <input className="itm-input" value={companyName || ""} disabled style={{ opacity: .7 }} />
          </div>

          <div className="form-group">
            <label className="itm-label">Nom du contrat</label>
            <input className="itm-input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex : Forfait annuel 2026" />
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="itm-label">Date de début</label>
              <input type="date" className="itm-input" value={form.startDate} onChange={e => set("startDate", e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="itm-label">Date de fin</label>
              <input type="date" className="itm-input" value={form.endDate} onChange={e => set("endDate", e.target.value)} />
            </div>
          </div>

          <div className="modal-checkbox-row">
            <label className="modal-checkbox">
              <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)} />
              Actif
            </label>
            <label className="modal-checkbox">
              <input type="checkbox" checked={form.isDefault} onChange={e => set("isDefault", e.target.checked)} />
              Définir comme contrat par défaut
            </label>
          </div>
          <label className="modal-checkbox" style={{ marginBottom: 4 }}>
            <input type="checkbox" checked={form.taxable} onChange={e => set("taxable", e.target.checked)} />
            Imposable
          </label>

          <div className="modal-section-divider" />
          <div className="modal-section-title"><i className="bi bi-tag me-2" />Plan et tarification</div>

          <div className="form-group">
            <label className="itm-label">Type de contrat</label>
            <select className="itm-input" value={form.type} onChange={e => set("type", e.target.value)}>
              <option value="HOUR_BANK">Banque d'heures</option>
              <option value="SERVICE_CONTRACT">Contrat de service</option>
              <option value="HOURLY">Horaire</option>
            </select>
          </div>

          {(form.type === "SERVICE_CONTRACT" || form.type === "HOURLY") && (
            <div className="form-group">
              <label className="itm-label">Période de facturation</label>
              <select className="itm-input" value={form.billingPeriod} onChange={e => set("billingPeriod", e.target.value)}>
                {Object.entries(PERIOD_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </div>
          )}

          {form.type === "HOURLY" && (
            <div className="form-group">
              <label className="itm-label">Taux horaire ($)</label>
              <input type="number" step="0.5" min="0" className="itm-input" value={form.hourlyRate} onChange={e => set("hourlyRate", e.target.value)} placeholder="100" />
            </div>
          )}

          {form.type === "HOUR_BANK" && (
            <>
              <div className="form-group">
                <label className="itm-label">Quota total d'heures</label>
                <input type="number" step="0.5" min="0" className="itm-input" value={form.totalHoursAllocated} onChange={e => set("totalHoursAllocated", e.target.value)} placeholder="20" />
              </div>
              <label className="modal-checkbox" style={{ marginBottom: 4 }}>
                <input type="checkbox" checked={form.rolloverEnabled} onChange={e => set("rolloverEnabled", e.target.checked)} />
                Heures reportables sur les périodes suivantes
              </label>
            </>
          )}

          <div className="modal-section-divider" />
          <div className="modal-section-title"><i className="bi bi-clipboard me-2" />Notes et champs personnalisés</div>

          <div className="form-group">
            <label className="itm-label">Notes (SLA, conditions particulières…)</label>
            <textarea className="itm-input" rows={3} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Ex : Plan d'Accord de Niveau de Service — intervention sous 4h ouvrées" style={{ resize: "vertical" }} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <><i className="bi bi-arrow-repeat spin me-1" />Création…</> : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  MODALE — Éditer un contrat
// ─────────────────────────────────────────────
function EditContractModal({ contract, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: contract.name || "",
    startDate: contract.startDate || "",
    endDate: contract.endDate || "",
    active: contract.active,
    isDefault: contract.isDefault || false,
    taxable: contract.taxable !== false,
    notes: contract.notes || "",
    totalHoursAllocated: contract.totalHoursAllocated ?? "",
    hourlyRate: contract.hourlyRate ?? "",
    rolloverEnabled: contract.rolloverEnabled,
    billingPeriod: contract.billingPeriod || (contract.type === "HOURLY" ? "ON_DEMAND" : "YEARLY"),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setError("");
    if (!form.name.trim()) return setError("Le nom est obligatoire.");

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        active: form.active,
        isDefault: form.isDefault,
        taxable: form.taxable,
        notes: form.notes,
        rolloverEnabled: form.rolloverEnabled,
        ...(contract.type === "SERVICE_CONTRACT" || contract.type === "HOURLY" ? { billingPeriod: form.billingPeriod } : {}),
        ...(contract.type === "HOUR_BANK" ? { totalHoursAllocated: parseFloat(form.totalHoursAllocated) || 0 } : {}),
        ...(contract.type === "HOURLY" ? { hourlyRate: parseFloat(form.hourlyRate) || 0 } : {}),
      };
      await updateContract(contract.id, payload);
      onSaved();
    } catch (err) {
      setError(err.message || "Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3>Modifier le contrat</h3>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>

        <div className="modal-body">
          {error && <div className="form-error"><i className="bi bi-exclamation-triangle me-2" />{error}</div>}

          <div className="modal-section-title"><i className="bi bi-file-earmark-text me-2" />Détails du contrat</div>

          <div className="form-group">
            <label className="itm-label">Client</label>
            <input className="itm-input" value={contract.company?.name || ""} disabled style={{ opacity: .7 }} />
          </div>

          <div className="form-group">
            <label className="itm-label">Nom du contrat</label>
            <input className="itm-input" value={form.name} onChange={e => set("name", e.target.value)} />
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="itm-label">Date de début</label>
              <input type="date" className="itm-input" value={form.startDate?.slice(0, 10) || ""} onChange={e => set("startDate", e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="itm-label">Date de fin</label>
              <input type="date" className="itm-input" value={form.endDate?.slice(0, 10) || ""} onChange={e => set("endDate", e.target.value)} />
            </div>
          </div>

          <div className="modal-checkbox-row">
            <label className="modal-checkbox">
              <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)} />
              Actif
            </label>
            <label className="modal-checkbox">
              <input type="checkbox" checked={form.isDefault} onChange={e => set("isDefault", e.target.checked)} />
              Définir comme contrat par défaut
            </label>
          </div>
          <label className="modal-checkbox" style={{ marginBottom: 4 }}>
            <input type="checkbox" checked={form.taxable} onChange={e => set("taxable", e.target.checked)} />
            Imposable
          </label>

          <div className="modal-section-divider" />
          <div className="modal-section-title"><i className="bi bi-tag me-2" />Plan et tarification</div>

          <div className="form-group">
            <label className="itm-label">Type de contrat</label>
            <input className="itm-input" value={TYPE_META[contract.type]?.label || contract.type} disabled style={{ opacity: .7 }} />
          </div>

          {(contract.type === "SERVICE_CONTRACT" || contract.type === "HOURLY") && (
            <div className="form-group">
              <label className="itm-label">Période de facturation</label>
              <select className="itm-input" value={form.billingPeriod} onChange={e => set("billingPeriod", e.target.value)}>
                {Object.entries(PERIOD_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </div>
          )}

          {contract.type === "HOURLY" && (
            <div className="form-group">
              <label className="itm-label">Taux horaire ($)</label>
              <input type="number" step="0.5" min="0" className="itm-input" value={form.hourlyRate} onChange={e => set("hourlyRate", e.target.value)} placeholder="100" />
            </div>
          )}

          {contract.type === "HOUR_BANK" && (
            <>
              <div className="form-group">
                <label className="itm-label">Quota total d'heures</label>
                <input type="number" step="0.5" min="0" className="itm-input" value={form.totalHoursAllocated} onChange={e => set("totalHoursAllocated", e.target.value)} />
              </div>
              <label className="modal-checkbox" style={{ marginBottom: 4 }}>
                <input type="checkbox" checked={form.rolloverEnabled} onChange={e => set("rolloverEnabled", e.target.checked)} />
                Heures reportables sur les périodes suivantes
              </label>
            </>
          )}

          <div className="modal-section-divider" />
          <div className="modal-section-title"><i className="bi bi-clipboard me-2" />Notes et champs personnalisés</div>

          <div className="form-group">
            <label className="itm-label">Notes (SLA, conditions particulières…)</label>
            <textarea className="itm-input" rows={3} value={form.notes} onChange={e => set("notes", e.target.value)} style={{ resize: "vertical" }} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <><i className="bi bi-arrow-repeat spin me-1" />Enregistrement…</> : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  MODALE — Allouer une tâche (recharge ou déduction manuelle)
// ─────────────────────────────────────────────
function AllocateModal({ contract, onClose, onSaved }) {
  const [mode, setMode] = useState("CONSUME");
  const [hours, setHours] = useState("");
  const [description, setDescription] = useState("");
  const [source, setSource] = useState("MANUAL_ADMIN");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");
    const h = parseFloat(hours);
    if (!h || h <= 0) return setError("Saisissez un nombre d'heures valide (supérieur à 0).");
    if (!description.trim()) return setError("La description est obligatoire.");

    setLoading(true);
    try {
      await addManualEntry(contract.id, {
        hours: mode === "CONSUME" ? -Math.abs(h) : Math.abs(h),
        description: description.trim(),
        source,
      });
      onSaved();
    } catch (err) {
      setError(err.message || "Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <h3>Allouer une tâche — {contract.name}</h3>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg" /></button>
        </div>

        <div className="modal-body">
          {error && <div className="form-error"><i className="bi bi-exclamation-triangle me-2" />{error}</div>}

          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <button
              onClick={() => setMode("CONSUME")}
              style={{ flex: 1, padding: "10px", borderRadius: 10, border: mode === "CONSUME" ? "2px solid #DC2626" : "1px solid var(--border)", background: mode === "CONSUME" ? "rgba(220,38,38,.06)" : "#fff", color: mode === "CONSUME" ? "#DC2626" : "var(--text-2)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              <i className="bi bi-dash-circle me-1" />Déduire
            </button>
            <button
              onClick={() => setMode("CREDIT")}
              style={{ flex: 1, padding: "10px", borderRadius: 10, border: mode === "CREDIT" ? "2px solid #16A34A" : "1px solid var(--border)", background: mode === "CREDIT" ? "rgba(22,163,74,.06)" : "#fff", color: mode === "CREDIT" ? "#16A34A" : "var(--text-2)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              <i className="bi bi-plus-circle me-1" />Recharger
            </button>
          </div>

          <div className="form-group">
            <label className="itm-label">Nombre d'heures</label>
            <input className="itm-input" type="number" step="0.25" min="0" value={hours} onChange={e => setHours(e.target.value)} placeholder="Ex: 2.5" />
          </div>

          <div className="form-group">
            <label className="itm-label">Description</label>
            <textarea className="itm-input" rows={3} value={description} onChange={e => setDescription(e.target.value)} placeholder="Ex: Intervention sur site — remplacement disque dur, hors ticket Atera" />
          </div>

          {mode === "CONSUME" && (
            <div className="form-group">
              <label className="itm-label">Origine</label>
              <select className="itm-input" value={source} onChange={e => setSource(e.target.value)}>
                <option value="MANUAL_ADMIN">Intervention manuelle</option>
                <option value="EMAIL_INVOICE_MANUAL">Courriel / facture externe</option>
              </select>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <><i className="bi bi-arrow-repeat spin me-1" />Enregistrement…</> : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  IMPRESSION — ouvre une fenêtre avec une vue imprimable du contrat
//
//  La fenêtre about:blank partage l'origine de l'app (et donc le token en
//  localStorage) : toute valeur venant de l'API doit passer par esc() avant
//  d'être injectée dans le HTML, sinon elle s'exécuterait comme du script.
// ─────────────────────────────────────────────
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, ch => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[ch]));

function printContract(row, entries, companyLabel) {
  const c = row.contract;
  const meta = TYPE_META[c.type] || { label: c.type };
  const remaining = Number(row.hoursRemaining) || 0;
  const total = Number(c.totalHoursAllocated) || 0;

  const entriesRows = entries.map(e => `
    <tr>
      <td>${new Date(e.createdAt).toLocaleDateString("fr-FR")}</td>
      <td>${esc(e.description)}</td>
      <td style="text-align:right; color:${Number(e.hours) < 0 ? "#DC2626" : "#16A34A"}; font-weight:600;">
        ${Number(e.hours) > 0 ? "+" : ""}${esc(e.hours)}h
      </td>
    </tr>`).join("");

  const html = `
    <!DOCTYPE html><html><head><meta charset="UTF-8"><title>Contrat — ${esc(c.name)}</title>
    <style>
      body { font-family: 'Segoe UI', Arial, sans-serif; color: #1A1A1A; padding: 40px; }
      h1 { font-size: 22px; margin-bottom: 4px; }
      .sub { color: #6B7280; font-size: 13px; margin-bottom: 24px; }
      .row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #E5E7EB; font-size: 13px; }
      .row b { color: #1A1A1A; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
      th { text-align: left; background: #F4F5F7; padding: 8px; font-size: 11px; text-transform: uppercase; color: #6B7280; }
      td { padding: 8px; border-bottom: 1px solid #F0F0F0; }
      .footer { margin-top: 30px; font-size: 11px; color: #9CA3AF; text-align: center; }
      @media print { body { padding: 20px; } }
    </style></head>
    <body>
      <h1>${esc(c.name)}</h1>
      <div class="sub">${esc(companyLabel)} — ${esc(meta.label)}</div>

      <div class="row"><span>Statut</span><b>${c.active ? "Actif" : "Inactif"}</b></div>
      <div class="row"><span>Date de début</span><b>${c.startDate ? new Date(c.startDate).toLocaleDateString("fr-FR") : "—"}</b></div>
      <div class="row"><span>Date d'expiration</span><b>${c.endDate ? new Date(c.endDate).toLocaleDateString("fr-FR") : "—"}</b></div>
      ${c.type === "HOUR_BANK" ? `
        <div class="row"><span>Quota total</span><b>${total}h</b></div>
        <div class="row"><span>Heures restantes</span><b>${remaining}h</b></div>
        <div class="row"><span>Report des heures</span><b>${c.rolloverEnabled ? "Activé" : "Désactivé"}</b></div>
      ` : c.type === "HOURLY" ? `
        <div class="row"><span>Taux horaire</span><b>${c.hourlyRate ? esc(c.hourlyRate) + " $/h" : "—"}</b></div>
        <div class="row"><span>Facturation</span><b>À la demande</b></div>
      ` : `
        <div class="row"><span>Période de facturation</span><b>${esc(PERIOD_LABELS[c.billingPeriod] || c.billingPeriod || "—")}</b></div>
      `}

      ${entries.length > 0 ? `
        <table>
          <tr><th>Date</th><th>Description</th><th style="text-align:right">Heures</th></tr>
          ${entriesRows}
        </table>
      ` : ""}

      <div class="footer">Document généré depuis Deskora — ${new Date().toLocaleDateString("fr-FR")}</div>
    </body></html>`;

  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────
export default function BillingPage() {
  const { can } = useAuth();
  const canManage = can("ADMIN_BILLING_MANAGE");

  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showAllocate, setShowAllocate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (companyId) loadContracts();
    else { setRows([]); setSelected(null); }
  }, [companyId]);

  const loadContracts = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getContractsForCompany(companyId);
      setRows(res.data || []);
    } catch (err) {
      setError(err.message || "Impossible de charger les contrats.");
    } finally {
      setLoading(false);
    }
  };

  const openContract = async (row) => {
    setSelected(row);
    setDateFrom("");
    setDateTo("");
    await loadEntries(row.contract.id, "", "");
  };

  const loadEntries = async (contractId, from, to) => {
    setEntriesLoading(true);
    try {
      const res = await getContractEntriesAdmin(contractId, from || undefined, to || undefined);
      setEntries(res.data || []);
    } catch {
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  const applyFilter = () => {
    if (selected) loadEntries(selected.contract.id, dateFrom, dateTo);
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Supprimer ce mouvement manuel ?")) return;
    try {
      await deleteManualEntry(entryId);
      loadEntries(selected.contract.id, dateFrom, dateTo);
      loadContracts();
    } catch (err) {
      alert(err.message || "Erreur lors de la suppression.");
    }
  };

  const handleAllocated = () => {
    setShowAllocate(false);
    loadEntries(selected.contract.id, dateFrom, dateTo);
    loadContracts();
  };

  const handleEdited = async () => {
    setShowEdit(false);
    await loadContracts();
  };

  const handleCreated = () => {
    setShowCreate(false);
    loadContracts();
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm("Supprimer ce contrat ainsi que tout son historique d'heures ? Cette action est irréversible.")) return;
    try {
      await deleteContract(contractId);
      if (selected?.contract?.id === contractId) setSelected(null);
      loadContracts();
    } catch (err) {
      alert(err.message || "Erreur lors de la suppression.");
    }
  };

  const handlePrint = (row) => {
    if (selected?.contract?.id === row.contract.id) {
      printContract(row, entries, companyName);
    } else {
      getContractEntriesAdmin(row.contract.id).then(res => {
        printContract(row, res.data || [], companyName);
      });
    }
  };

  return (
    <>
      <Topbar title="Facturation" subtitle="clients" admin />
      <div className="page-body">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
          <div style={{ maxWidth: 360, flex: 1 }}>
            <CompanySelector selectedId={companyId} onChange={company => { setCompanyId(company?.id || ""); setCompanyName(company?.name || ""); }} />
          </div>
          {companyId && canManage && (
            <button className="btn-itm btn-itm-primary" onClick={() => setShowCreate(true)}>
              <i className="bi bi-plus-lg me-1" />Nouveau contrat
            </button>
          )}
        </div>

        {!companyId && (
          <div className="itm-card" style={{ textAlign: "center", padding: 60, color: "var(--text-3)" }}>
            <i className="bi bi-building" style={{ fontSize: 28, marginBottom: 10, display: "block" }} />
            Sélectionnez une entreprise pour voir ses contrats de facturation.
          </div>
        )}

        {companyId && (
          <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 1.2fr" : "1fr", gap: 20 }}>

            <div>
              {loading && <div className="itm-card" style={{ textAlign: "center", padding: 40, color: "var(--text-3)" }}>Chargement…</div>}

              {error && (
                <div style={{ background: "rgba(220,38,38,.08)", border: "1px solid rgba(220,38,38,.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#DC2626" }}>
                  <i className="bi bi-exclamation-triangle-fill me-2" />{error}
                </div>
              )}

              {!loading && !error && rows.length === 0 && (
                <div className="itm-card" style={{ textAlign: "center", padding: 40, color: "var(--text-3)" }}>
                  Aucun contrat pour cette entreprise. Lancez une synchronisation Atera si des contrats existent là-bas.
                </div>
              )}

              {rows.map(row => {
                const c = row.contract;
                const meta = TYPE_META[c.type] || { label: c.type, icon: "bi-file-earmark", color: "#6B7280" };
                const isHourBank = c.type === "HOUR_BANK";
                const remaining = Number(row.hoursRemaining) || 0;
                const total = Number(c.totalHoursAllocated) || 0;
                const pct = isHourBank && total > 0 ? Math.max(0, Math.min(100, (remaining / total) * 100)) : 0;
                const isSelected = selected?.contract?.id === c.id;

                return (
                  <div
                    key={c.id}
                    className="itm-card"
                    style={{ marginBottom: 14, padding: 20, border: isSelected ? "2px solid var(--accent)" : "1px solid var(--border)" }}
                  >
                    <div onClick={() => openContract(row)} style={{ cursor: "pointer" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 38, height: 38, borderRadius: 10, background: meta.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: 16 }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text-1)" }}>{c.name}</div>
                            <div style={{ fontSize: 11, color: meta.color, fontWeight: 600 }}>{meta.label}</div>
                          </div>
                        </div>
                        <span className={`badge-status ${c.active ? "bs-active" : "bs-open"}`}>{c.active ? "Actif" : "Inactif"}</span>
                      </div>

                      {isHourBank ? (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-2)", marginBottom: 6 }}>
                            <span>{fmtHours(remaining)} restantes</span>
                            <span>/ {fmtHours(total)}</span>
                          </div>
                          <div style={{ height: 8, background: "var(--navy-soft)", borderRadius: 99, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${pct}%`, background: pct < 20 ? "#DC2626" : "var(--accent)", borderRadius: 99 }} />
                          </div>
                        </>
                      ) : c.type === "HOURLY" ? (
                        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                          <i className="bi bi-stopwatch me-1" />
                          {c.hourlyRate ? `${c.hourlyRate} $/h — facturé à la demande` : "Facturé à la demande"}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                          <i className="bi bi-arrow-repeat me-1" />Renouvellement {PERIOD_LABELS[c.billingPeriod]?.toLowerCase() || ""}
                        </div>
                      )}

                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-3)", marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                        <span>Début : {fmtDate(c.startDate)}</span>
                        <span>Expire le : {fmtDate(c.endDate)}</span>
                      </div>
                    </div>

                    {/* Actions — enfin fonctionnelles */}
                    <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                      {canManage && (
                        <button
                          onClick={() => { setSelected(row); setShowEdit(true); }}
                          style={{ flex: 1, padding: "7px", borderRadius: 8, border: "1px solid var(--border)", background: "#fff", color: "var(--text-2)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          <i className="bi bi-pencil me-1" />Modifier
                        </button>
                      )}
                      <button
                        onClick={() => handlePrint(row)}
                        style={{ flex: 1, padding: "7px", borderRadius: 8, border: "1px solid var(--border)", background: "#fff", color: "var(--text-2)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                      >
                        <i className="bi bi-printer me-1" />Imprimer
                      </button>
                      {canManage && (
                        <button
                          onClick={() => handleDeleteContract(c.id)}
                          title="Supprimer le contrat"
                          style={{ padding: "7px 10px", borderRadius: 8, border: "1px solid rgba(220,38,38,.2)", background: "#fff", color: "#DC2626", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          <i className="bi bi-trash3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {selected && (
              <div className="itm-card" style={{ padding: 20, height: "fit-content", position: "sticky", top: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <div className="card-title" style={{ marginBottom: 0 }}>Historique — {selected.contract.name}</div>
                  <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 16 }}>
                    <i className="bi bi-x-lg" />
                  </button>
                </div>

                {canManage && selected.contract.type === "HOUR_BANK" && (
                  <button className="btn-itm btn-itm-primary" onClick={() => setShowAllocate(true)} style={{ width: "100%", marginBottom: 16 }}>
                    <i className="bi bi-plus-circle me-1" />Allouer une tâche
                  </button>
                )}

                <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <label className="itm-label">Du</label>
                    <input type="date" className="itm-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="itm-label">Au</label>
                    <input type="date" className="itm-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                  </div>
                  <button className="btn-itm btn-itm-primary" onClick={applyFilter} style={{ height: 38 }}>
                    <i className="bi bi-funnel" />
                  </button>
                </div>

                {entriesLoading && <div style={{ textAlign: "center", padding: 20, color: "var(--text-3)" }}>Chargement…</div>}

                {!entriesLoading && entries.length === 0 && (
                  <div style={{ textAlign: "center", padding: 20, color: "var(--text-3)", fontSize: 13 }}>Aucun mouvement sur cette période.</div>
                )}

                {!entriesLoading && entries.map(e => (
                  <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                    <div>
                      <div style={{ fontSize: 13, color: "var(--text-1)" }}>{e.description}</div>
                      <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                        {new Date(e.createdAt).toLocaleDateString("fr-FR")}
                        {e.source === "ATERA_TICKET_SYNC" && " · Ticket Atera"}
                        {e.source === "MANUAL_ADMIN" && " · Intervention manuelle"}
                        {e.source === "EMAIL_INVOICE_MANUAL" && " · Courriel/facture"}
                        {e.createdBy && ` · ${e.createdBy.firstName} ${e.createdBy.lastName}`}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: Number(e.hours) < 0 ? "#DC2626" : "#16A34A", whiteSpace: "nowrap" }}>
                        {Number(e.hours) > 0 ? "+" : ""}{fmtHours(e.hours)}
                      </div>
                      {canManage && e.source !== "ATERA_TICKET_SYNC" && (
                        <button onClick={() => handleDeleteEntry(e.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 13 }} title="Supprimer">
                          <i className="bi bi-trash3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showAllocate && selected && (
        <AllocateModal contract={selected.contract} onClose={() => setShowAllocate(false)} onSaved={handleAllocated} />
      )}
      {showEdit && selected && (
        <EditContractModal contract={selected.contract} onClose={() => setShowEdit(false)} onSaved={handleEdited} />
      )}
      {showCreate && (
        <CreateContractModal companyId={companyId} companyName={companyName} onClose={() => setShowCreate(false)} onSaved={handleCreated} />
      )}

      <style>{CSS}</style>
    </>
  );
}

// ─────────────────────────────────────────────
//  STYLES — cette page ne dépend d'aucun CSS global
//  pour les modales : tout est défini ici.
// ─────────────────────────────────────────────
const CSS = `
.modal-section-title{font-size:12px;font-weight:700;color:#1A1A1A;text-transform:uppercase;letter-spacing:.05em;display:flex;align-items:center;margin:4px 0 14px}
.modal-section-title i{color:#9CA3AF;font-size:14px}
.modal-section-divider{height:1px;background:rgba(0,0,0,.08);margin:18px 0}
.modal-checkbox-row{display:flex;gap:18px;flex-wrap:wrap;margin-bottom:8px}
.modal-checkbox{display:flex;align-items:center;gap:8px;font-size:13px;color:#4B5563;cursor:pointer}
.modal-overlay{position:fixed;inset:0;z-index:1055;background:rgba(26,26,26,.6);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px;animation:billModalFade .15s ease}
@keyframes billModalFade{from{opacity:0}to{opacity:1}}
.modal-box{background:#fff;border:1px solid rgba(0,0,0,.08);border-radius:16px;box-shadow:0 32px 100px rgba(0,0,0,.25);width:100%;max-width:460px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;animation:billModalSlide .2s cubic-bezier(.34,1.56,.64,1)}
@keyframes billModalSlide{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px 14px;border-bottom:1px solid rgba(0,0,0,.08)}
.modal-header h3{font-size:15px;font-weight:700;color:#1A1A1A;margin:0}
.modal-close{background:#F4F5F7;border:none;cursor:pointer;color:#4B5563;width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:12px}
.modal-close:hover{background:#E63329;color:#fff}
.modal-body{padding:18px 20px;overflow-y:auto;flex:1;display:flex;flex-direction:column;gap:0}
.modal-footer{padding:14px 20px;border-top:1px solid rgba(0,0,0,.08);display:flex;justify-content:flex-end;gap:10px}
.form-group{margin-bottom:14px}
.form-error{background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.25);border-radius:8px;color:#DC2626;font-size:12px;padding:8px 12px;margin-bottom:14px}
.btn-primary{background:#E63329;border:none;border-radius:10px;color:#fff;font-size:13px;font-weight:600;padding:8px 18px;cursor:pointer;display:flex;align-items:center;gap:6px;transition:filter .15s}
.btn-primary:hover{filter:brightness(1.1)}
.btn-primary:disabled{opacity:.5;cursor:not-allowed}
.btn-secondary{background:#fff;border:1px solid rgba(0,0,0,.1);border-radius:10px;color:#4B5563;font-size:13px;padding:8px 18px;cursor:pointer;transition:all .15s}
.btn-secondary:hover{background:#F4F5F7;color:#1A1A1A}
.itm-label{display:block;font-size:.78rem;font-weight:600;color:#4B5563;margin-bottom:5px;letter-spacing:.03em;text-transform:uppercase}
.itm-input{display:block;width:100%;background:#F4F5F7;border:1px solid rgba(0,0,0,.1);border-radius:8px;color:#1A1A1A;padding:9px 12px;font-size:.88rem;transition:border-color .18s,box-shadow .18s;outline:none}
.itm-input:focus{border-color:#E63329;box-shadow:0 0 0 3px rgba(230,51,41,.15)}
.itm-input::placeholder{color:#9CA3AF}
`;